import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useData } from '../../context/DataContext';
import ClientDetails from './ClientDetails';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiPhone, FiDollarSign, FiCalendar, FiEye, FiEdit2, FiUserCheck } = FiIcons;

const ClientList = ({ clients, onEditClient }) => {
  const { updateClient } = useData();
  const [selectedClient, setSelectedClient] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'lead':
        return 'bg-blue-100 text-blue-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'lead':
        return 'bg-yellow-100 text-yellow-800';
      case 'proposal':
        return 'bg-blue-100 text-blue-800';
      case 'negotiation':
        return 'bg-purple-100 text-purple-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStageChange = (clientId, newStage) => {
    updateClient(clientId, { stage: newStage });
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Client
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Contact
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Value
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Stage
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Last Contact
                </th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {clients.map((client, index) => (
                  <motion.tr
                    key={client.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{client.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(client.status)}`}>
                          {client.status}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <SafeIcon icon={FiMail} className="w-4 h-4" />
                          <span>{client.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <SafeIcon icon={FiPhone} className="w-4 h-4" />
                          <span>{client.phone}</span>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1 text-gray-900">
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                        <span className="font-medium">
                          {client.value?.toLocaleString() || '0'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <select
                        value={client.stage}
                        onChange={(e) => handleStageChange(client.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="lead">Lead</option>
                        <option value="proposal">Proposal</option>
                        <option value="negotiation">Negotiation</option>
                        <option value="active">Active</option>
                      </select>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(client.lastContact), { addSuffix: true })}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setSelectedClient(client)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </motion.button>
                        
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onEditClient(client)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {clients.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUserCheck} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
            <p className="text-gray-600">
              Add your first client or adjust the filters to see results.
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedClient && (
          <ClientDetails
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ClientList;